import { Product, ProductSlug } from "@/api/product-api";
import { useProducts } from "@/services/product/useProductService";
import { useMemo } from "react";
import { create } from "zustand";
import { analyticsInstance } from "@/libs/analytics";
import { saveTactSuitRecommendResponse } from "@/api/tactsuit-recommend-api";
import { usePricedFilter } from "@/hooks/usePricedFilter";

// https://www.notion.so/bhaptics/2956d5724b8b8035b902d97389738f59?source=copy_link
const PRODUCT_SCORES = {
  // 관심사 (가중치: x4)
  pcgames: { Pro: 8, Air: 12, Sleeve: 0, Visor: 0, Feet: 0 },
  vrgames: { Pro: 12, Air: 8, Sleeve: 12, Visor: 8, Feet: 8 },
  consolegames: { Pro: 12, Air: 8, Sleeve: 8, Visor: 0, Feet: 0 },
  simracinggames: { Pro: 8, Air: 12, Sleeve: 4, Visor: 0, Feet: 0 },
  listeningtomusic: { Pro: 8, Air: 12, Sleeve: 4, Visor: 0, Feet: 0 },
  watchingmovies: { Pro: 12, Air: 8, Sleeve: 4, Visor: 0, Feet: 0 },
  interestother: { Pro: 0, Air: 0, Sleeve: 0, Visor: 0, Feet: 0 },

  // 장르 (가중치: x2)
  action: { Pro: 6, Air: 4, Sleeve: 2, Visor: 2, Feet: 0 },
  adventure: { Pro: 4, Air: 4, Sleeve: 2, Visor: 2, Feet: 0 },
  shooter: { Pro: 6, Air: 4, Sleeve: 6, Visor: 2, Feet: 0 },
  simulation: { Pro: 6, Air: 4, Sleeve: 2, Visor: 0, Feet: 0 },
  sports: { Pro: 4, Air: 4, Sleeve: 4, Visor: 0, Feet: 2 },
  rhythm: { Pro: 6, Air: 4, Sleeve: 6, Visor: 0, Feet: 2 },
  rpg: { Pro: 6, Air: 4, Sleeve: 4, Visor: 0, Feet: 0 },
  horror: { Pro: 6, Air: 4, Sleeve: 2, Visor: 2, Feet: 0 },
  strategy: { Pro: 4, Air: 4, Sleeve: 2, Visor: 2, Feet: 0 },
  puzzle: { Pro: 6, Air: 4, Sleeve: 2, Visor: 2, Feet: 0 },
  sandbox: { Pro: 6, Air: 4, Sleeve: 2, Visor: 0, Feet: 0 },
  openworld: { Pro: 6, Air: 4, Sleeve: 4, Visor: 0, Feet: 0 },
  singleplayer: { Pro: 6, Air: 4, Sleeve: 2, Visor: 0, Feet: 0 },
  genreother: { Pro: 0, Air: 0, Sleeve: 0, Visor: 0, Feet: 0 },

  // 기능 (가중치: x3)
  detailedhapticfeedback: { Pro: 9, Air: 6, Sleeve: 3, Visor: 3, Feet: 3 },
  lightweight: { Pro: 3, Air: 9, Sleeve: 3, Visor: 3, Feet: 3 },
  batterylife: { Pro: 6, Air: 6, Sleeve: 6, Visor: 6, Feet: 6 },
  sizeoptions: { Pro: 6, Air: 3, Sleeve: 6, Visor: 3, Feet: 3 },
  stylishdesign: { Pro: 9, Air: 6, Sleeve: 0, Visor: 0, Feet: 0 },
  independenthapticcontrol: { Pro: 9, Air: 0, Sleeve: 0, Visor: 0, Feet: 0 },
  variouscoloroptions: { Pro: 0, Air: 9, Sleeve: 0, Visor: 0, Feet: 0 },
  featureother: { Pro: 0, Air: 0, Sleeve: 0, Visor: 0, Feet: 0 },
} as const;

type Id = keyof typeof PRODUCT_SCORES;
type ProductType = "Pro" | "Air" | "Sleeve" | "Visor" | "Feet";

interface Option {
  id: Id;
  label: string;
  selected: boolean;
  timestamp?: number;
  goto?: number;
  /**
   * Other 옵션 전용 — 사용자가 확정한 자유 입력 칩 목록.
   * Enter 또는 쉼표 입력 시 input text 가 trim 후 여기에 push 된다.
   * 다른 옵션에는 undefined.
   */
  otherTexts?: string[];
  /** Other 옵션 전용 — 아직 칩으로 commit 되지 않은 input 의 미확정 텍스트. submit 시 자동 commit. */
  otherInputText?: string;
}

/** 자유 입력을 받아야 하는 "Other" 옵션 id 집합. 화이트리스트 동기화는 mgmt 백엔드와 정렬. */
const OTHER_OPTION_IDS: ReadonlySet<Id> = new Set([
  "interestother",
  "genreother",
  "featureother",
] as const);

/** Other 자유 입력 텍스트(칩 1개)의 최대 글자수. mgmt 의 MAX_OPTION_LEN(64) 과 정렬. */
export const OTHER_TEXT_MAX_LEN = 64;

/** Other 자유 입력 칩 개수 상한. mgmt MAX_OPTIONS=20 보호 + UI 폭 보호. */
export const OTHER_CHIPS_MAX = 5;

const TYPE_TO_SLUG_PRODUCT_MAP: Record<ProductType, ProductSlug> = {
  Pro: ProductSlug.TACTSUIT_PRO,
  Air: ProductSlug.TACTSUIT_AIR,
  Sleeve: ProductSlug.TACTSLEEVE,
  Visor: ProductSlug.TACTVISOR,
  Feet: ProductSlug.TACTOSY_FOR_FEET,
};

interface Step {
  id: string;
  options: Option[];
}

interface Store {
  stepIndex: number;
  steps: Step[];
  toggleOption: (optionId: string) => void;
  /** Other 옵션 input 의 미확정 텍스트 설정 (64자로 잘라 저장). */
  setOtherInputText: (optionId: string, text: string) => void;
  /** 미확정 input 텍스트를 trim/cap 후 칩으로 commit. 빈/중복/상한 도달 시 input 만 비우고 무시. */
  commitOtherChip: (optionId: string) => void;
  /** 특정 인덱스의 칩 제거 (보통 × 버튼 또는 빈 input 에서 Backspace). */
  removeOtherChip: (optionId: string, index: number) => void;
  next: () => void;
  prev: () => void;
  goTo: (step: number) => void;
  clear: () => void;
  getSelectedOptions: () => Option[];
  getCurrentStep: () => Step;
  getCurrentSelectedOptions: () => Option[];
}

const INITIAL_STEPS: Step[] = [
  {
    id: "interests",
    options: [
      { id: "pcgames", label: "PC Games", selected: false },
      { id: "vrgames", label: "VR Games", selected: false },
      { id: "consolegames", label: "Console Games", selected: false },
      { id: "simracinggames", label: "Sim Racing Games", selected: false },
      {
        id: "listeningtomusic",
        label: "Listening to Music",
        selected: false,
        goto: 2, //. 해당 옵션만 선택되었을때 바로 features 선택으로 이동
      },
      {
        id: "watchingmovies",
        label: "Watching Movies",
        selected: false,
        goto: 2, // 해당 옵션만 선택되었을때 바로 features 선택으로 이동
      },
      {
        id: "interestother",
        label: "Other",
        selected: false,
        otherTexts: [],
        otherInputText: "",
        goto: 2, // 게임 카테고리가 아니므로 장르 단계 스킵
      },
    ],
  },
  {
    id: "genres",
    options: [
      { id: "action", label: "Action", selected: false },
      { id: "adventure", label: "Adventure", selected: false },
      { id: "shooter", label: "Shooter", selected: false },
      { id: "simulation", label: "Simulation", selected: false },
      { id: "sports", label: "Sports", selected: false },
      { id: "rhythm", label: "Rhythm", selected: false },
      { id: "rpg", label: "RPG", selected: false },
      { id: "horror", label: "Horror", selected: false },
      { id: "strategy", label: "Strategy", selected: false },
      { id: "puzzle", label: "Puzzle", selected: false },
      { id: "sandbox", label: "Sandbox", selected: false },
      { id: "openworld", label: "Open World", selected: false },
      { id: "singleplayer", label: "Single Player", selected: false },
      {
        id: "genreother",
        label: "Other",
        selected: false,
        otherTexts: [],
        otherInputText: "",
      },
    ],
  },
  {
    id: "features",
    options: [
      {
        id: "detailedhapticfeedback",
        label: "Detailed Haptic Feedback",
        selected: false,
      },
      { id: "lightweight", label: "Lightweight", selected: false },
      { id: "batterylife", label: "Battery Life", selected: false },
      { id: "sizeoptions", label: "Size Options", selected: false },
      { id: "stylishdesign", label: "Stylish Design", selected: false },
      {
        id: "independenthapticcontrol",
        label: "Independent Haptic Control",
        selected: false,
      },
      {
        id: "variouscoloroptions",
        label: "Various Color Options",
        selected: false,
      },
      {
        id: "featureother",
        label: "Other",
        selected: false,
        otherTexts: [],
        otherInputText: "",
      },
    ],
  },
];

const SCORE_THRESHOLDS = {
  MIN_EXPOSURE: 15,
  BEST_MATCH: 55,
} as const;

const getRecommendation = (selectedOptions: Option[]) => {
  const products: ProductType[] = ["Pro", "Air", "Sleeve", "Visor", "Feet"];
  const scores = products.reduce(
    (acc, product) => {
      acc[product] = 0;
      return acc;
    },
    {} as Record<ProductType, number>,
  );

  selectedOptions.forEach((option) => {
    const scoreData = PRODUCT_SCORES[option.id as keyof typeof PRODUCT_SCORES];
    if (scoreData) {
      products.forEach((product) => {
        scores[product] += scoreData[product];
      });
    }
  });

  const relevantProducts = products
    .map((product) => ({
      product,
      score: scores[product],
      isBestMatch: scores[product] >= SCORE_THRESHOLDS.BEST_MATCH,
    }))
    .filter((item) => item.score >= SCORE_THRESHOLDS.MIN_EXPOSURE)
    .sort((a, b) => b.score - a.score)
    .map((item, index) => {
      if (index === 0) {
        return item;
      }
      return { ...item, isBestMatch: false };
    });

  return {
    products: relevantProducts,
    keywords: selectedOptions.map((o) => o.label),
  };
};

const limitSelectedOptions = (options: Option[], maxCount: number = 2) => {
  const selected = options
    .filter((o) => o.selected)
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  if (selected.length <= maxCount) return options;

  // 가장 최신 maxCount개만 유지
  const keepTimestamps = new Set(
    selected.slice(-maxCount).map((o) => o.timestamp),
  );

  return options.map((option) =>
    option.selected && !keepTimestamps.has(option.timestamp)
      ? { ...option, selected: false }
      : option,
  );
};

const store = create<Store>((set, get) => ({
  stepIndex: 0,
  steps: [...INITIAL_STEPS],
  getSelectedOptions: () => {
    const state = get();
    return state.steps.flatMap((step) =>
      step.options.filter((o) => o.selected),
    );
  },
  getCurrentStep: () => {
    const state = get();
    return state.steps[state.stepIndex];
  },
  getCurrentSelectedOptions: () => {
    const currentStep = get().getCurrentStep();
    if (!currentStep) return [];
    return currentStep.options.filter((o) => o.selected);
  },
  toggleOption: (optionId: string) =>
    set((state) => {
      const isLastStep = state.stepIndex === state.steps.length - 1;

      const steps = state.steps.map((step, index) => {
        if (index !== state.stepIndex) return step;

        let options = step.options.map((option) => {
          if (option.id !== optionId) return option;
          const nextSelected = !option.selected;
          // Other 옵션 해제 시 자유 입력 칩과 미확정 input 텍스트도 함께 초기화 —
          // 다시 켰을 때 이전 입력이 무심코 전송되는 일을 막는다.
          if (OTHER_OPTION_IDS.has(option.id) && !nextSelected) {
            return {
              ...option,
              selected: false,
              timestamp: +new Date(),
              otherTexts: [],
              otherInputText: "",
            };
          }
          return {
            ...option,
            selected: nextSelected,
            timestamp: +new Date(),
          };
        });

        // 마지막 스텝에서는 최대 2개만 선택 가능
        if (isLastStep) {
          options = limitSelectedOptions([...options], 2);
        }

        return { ...step, options };
      });

      return { steps };
    }),
  setOtherInputText: (optionId: string, text: string) =>
    set((state) => {
      const trimmed = text.slice(0, OTHER_TEXT_MAX_LEN);
      const steps = state.steps.map((step, index) => {
        if (index !== state.stepIndex) return step;
        if (!step.options.some((o) => o.id === optionId)) return step;
        const options = step.options.map((option) =>
          option.id === optionId && OTHER_OPTION_IDS.has(option.id)
            ? { ...option, otherInputText: trimmed }
            : option,
        );
        return { ...step, options };
      });
      return { steps };
    }),
  commitOtherChip: (optionId: string) =>
    set((state) => {
      const steps = state.steps.map((step, index) => {
        if (index !== state.stepIndex) return step;
        if (!step.options.some((o) => o.id === optionId)) return step;
        const options = step.options.map((option) => {
          if (option.id !== optionId || !OTHER_OPTION_IDS.has(option.id))
            return option;
          const chips = option.otherTexts ?? [];
          const raw = (option.otherInputText ?? "").trim();
          // 빈 / 중복 / 상한 도달 → input 만 비우고 칩은 변경 없음 (silent ignore)
          if (
            raw.length === 0 ||
            chips.includes(raw) ||
            chips.length >= OTHER_CHIPS_MAX
          ) {
            return { ...option, otherInputText: "" };
          }
          const trimmed = raw.slice(0, OTHER_TEXT_MAX_LEN);
          return {
            ...option,
            otherTexts: [...chips, trimmed],
            otherInputText: "",
          };
        });
        return { ...step, options };
      });
      return { steps };
    }),
  removeOtherChip: (optionId: string, indexToRemove: number) =>
    set((state) => {
      const steps = state.steps.map((step, sIdx) => {
        if (sIdx !== state.stepIndex) return step;
        if (!step.options.some((o) => o.id === optionId)) return step;
        const options = step.options.map((option) => {
          if (option.id !== optionId || !OTHER_OPTION_IDS.has(option.id))
            return option;
          const chips = option.otherTexts ?? [];
          if (indexToRemove < 0 || indexToRemove >= chips.length) return option;
          return {
            ...option,
            otherTexts: chips.filter((_, i) => i !== indexToRemove),
          };
        });
        return { ...step, options };
      });
      return { steps };
    }),
  next: () =>
    set((state) => {
      const selectedOptions = get().getCurrentSelectedOptions();
      const everyOptionHasGoto = selectedOptions.every(
        (o) => o.goto !== undefined,
      );
      const gotoOption = selectedOptions?.find((o) => o.goto !== undefined);

      if (everyOptionHasGoto && gotoOption?.goto !== undefined) {
        return { stepIndex: Math.min(gotoOption.goto, state.steps.length - 1) };
      }

      return {
        stepIndex: Math.min(state.stepIndex + 1, state.steps.length - 1),
      };
    }),

  prev: () =>
    set((state) => {
      const prevSteps = state.steps.slice(0, state.stepIndex);
      const lastSelectedStepIndex = prevSteps.findLastIndex((step) =>
        step.options.some((o) => o.selected),
      );

      const targetStepIndex =
        lastSelectedStepIndex !== -1
          ? lastSelectedStepIndex
          : Math.max(state.stepIndex - 1, 0);

      // 목표 스텝 이후의 모든 스텝 초기화
      const steps = state.steps.map((step, index) => {
        if (index > targetStepIndex) {
          return {
            ...step,
            options: step.options.map((option) => ({
              ...option,
              selected: false,
            })),
          };
        }
        return step;
      });

      return {
        stepIndex: targetStepIndex,
        steps,
      };
    }),

  goTo: (step: number) =>
    set((state) => ({
      stepIndex: Math.max(0, Math.min(step, state.steps.length - 1)),
    })),

  clear: () =>
    set(() => ({
      stepIndex: 0,
      steps: [...INITIAL_STEPS],
    })),
}));

const filterRecommendation = (
  products: ReturnType<typeof getRecommendation>["products"],
  data: Product[] | undefined,
) => {
  /**
   * Pro·Air 가 둘 다 노출되면 **점수 높은 쪽만** 남긴다 (#5505).
   *
   * ## 무엇이 틀렸었나
   *
   * 종전 코드는 *"상위 제품인 Pro만 보여줌"* 이라며 **점수와 무관하게 Air 를 버렸다.**
   * 그래서 Air 가 1위인 조합에서도 Pro 가 추천됐다 — 신고된 사례:
   *
   * ```
   * PC Games > Action > Lightweight
   *   Air 12+4+9 = 25   ← 정렬 1위
   *   Pro  8+6+3 = 17
   *   → 종전: Air 가 제거되어 Pro 가 표시
   * ```
   *
   * 점수 표·합산·`≥15` 필터·내림차순 정렬은 전부 정상이었다. 결함은 **정렬 뒤의 이 필터**다.
   *
   * ## 스펙과의 차이
   *
   * 기획 문서(위 Notion 링크)에 「Pro 우선」 규칙은 **없다.** Pro/Air 를 함께 다루는 규칙은
   * **Best Match 라벨** 하나뿐이다:
   *
   * > 강력 추천 점수 ≥ 55점 — "Best Match" 라벨 부여
   * > \* Pro/Air 둘다 55점 넘을 시, **둘중에 한점 만 best match로 부여**
   *
   * 즉 스펙은 **라벨을 하나만** 주라고 했고, 그 처리는 `getRecommendation` 이 이미 한다
   * (`index === 0` 만 `isBestMatch` 유지). 종전 필터는 **제품을 목록에서 제거**했고 조건도
   * `≥55` 가 아니라 `≥15`(노출 기준)였다 — 중복이자 스펙 초과였다.
   *
   * ## 「하나만 보여준다」는 유지한다
   *
   * 주석이 그 의도를 명시했으므로 필터를 지우지 않고 **기준만 점수로** 바꿨다. 신고가 문제
   * 삼은 것은 「Pro 로 **고정**」이지 「둘 다 안 보인다」가 아니다.
   *
   * ⚠️ 동점이면 `products` 배열 순서(`Pro` 가 먼저)에 따라 **Pro** 가 남는다. `sort` 는
   * 안정 정렬이고 그 배열이 `["Pro","Air",…]` 이므로 결정적이다 — 가드가 이 동점 처리를
   * 함께 잠근다.
   */
  const pro = products.find((p) => p.product === "Pro");
  const air = products.find((p) => p.product === "Air");
  const droppedType: ProductType | null =
    pro && air ? (air.score > pro.score ? "Pro" : "Air") : null;

  return products
    .filter((p) => p.product !== droppedType)
    .map((p) => {
      const product = data?.find(
        (prod) => prod.slug === TYPE_TO_SLUG_PRODUCT_MAP[p.product],
      );
      return { ...p, product };
    })
    .filter((p) => p.product !== undefined)
    .slice(0, 3); // 최대 3개까지만
};

export const useRecommendTactSuitStore = () => {
  const stepIndex = store((state) => state.stepIndex);
  const next = store((state) => state.next);
  const prev = store((state) => state.prev);
  const goTo = store((state) => state.goTo);
  const steps = store((state) => state.steps);
  const toggleOption = store((state) => state.toggleOption);
  const setOtherInputText = store((state) => state.setOtherInputText);
  const commitOtherChip = store((state) => state.commitOtherChip);
  const removeOtherChip = store((state) => state.removeOtherChip);
  const clear = store((state) => state.clear);
  const getSelectedOptions = store((state) => state.getSelectedOptions);
  const { data: allProducts } = useProducts();
  // 추천 후보도 판매 목록이다 — 이 국가·통화에서 가격 없는 제품은 추천하지 않는다.
  const priced = usePricedFilter();
  const data = useMemo(() => priced(allProducts), [priced, allProducts]);

  const recommendation = useMemo(() => {
    const result = getRecommendation(getSelectedOptions());
    const processedProducts = filterRecommendation(result.products, data);
    const slugs = processedProducts
      .map((p) => p.product?.slug)
      .filter(Boolean) as string[];

    return {
      ...result,
      slugs,
      products: processedProducts,
    };
  }, [getSelectedOptions(), data]);

  // "Next" 활성 조건: 적어도 한 옵션이 선택돼야 하고, 선택된 Other 옵션은 칩이 1개 이상이거나
  // input 미확정 텍스트가 비어있지 않아야 한다. (둘 다 비어있으면 빈 페이로드 → 차단)
  const someCurrentStepSelected = useMemo(() => {
    const selected = steps[stepIndex]?.options.filter((o) => o.selected) ?? [];
    if (selected.length === 0) return false;
    return selected.every((o) => {
      if (!OTHER_OPTION_IDS.has(o.id)) return true;
      const chips = o.otherTexts ?? [];
      const pending = (o.otherInputText ?? "").trim();
      return chips.length > 0 || pending.length > 0;
    });
  }, [steps, stepIndex]);

  const reportRecommendedTactSuits = (ctx: {
    locale: string;
    bearerToken?: string;
  }) => {
    const { keywords, slugs } = recommendation;
    analyticsInstance?.reportRecommendedTactSuits({
      keywords,
      productSlugs: slugs,
    });
    // Other 옵션은 라벨("Other") 대신 사용자가 입력한 자유 텍스트(칩 + 미확정 input) 로
    // 치환해 보낸다. submit 시점에 input 에 아직 commit 안 된 텍스트가 있으면 자동으로
    // 칩에 합치되, 중복은 제외. 백엔드는 화이트리스트 외 라벨을 Other 그룹으로 자동 분류한다.
    const selectedLabels = (stepId: string) =>
      steps
        .find((s) => s.id === stepId)
        ?.options.filter((o) => o.selected)
        .flatMap((o) => {
          if (!OTHER_OPTION_IDS.has(o.id)) return [o.label];
          const chips = o.otherTexts ?? [];
          const pending = (o.otherInputText ?? "").trim();
          const result = [...chips];
          if (pending && !result.includes(pending)) result.push(pending);
          // mgmt MAX_OPTIONS=20 보호. UI 측 OTHER_CHIPS_MAX=5 가 일반적으로 더 엄격하므로 안전.
          return result.slice(0, OTHER_CHIPS_MAX);
        }) ?? [];
    saveTactSuitRecommendResponse({
      platforms: selectedLabels("interests"),
      genres: selectedLabels("genres"),
      purchaseFeatures: selectedLabels("features"),
      locale: ctx.locale,
      bearerToken: ctx.bearerToken,
    });
  };

  return {
    stepIndex,
    next,
    prev,
    goTo,
    steps,
    toggleOption,
    setOtherInputText,
    commitOtherChip,
    removeOtherChip,
    clear,
    selectedOptions: getSelectedOptions(),
    someCurrentStepSelected,
    recommendation,
    reportRecommendedTactSuits,
    isOtherOption: (id: string): boolean => OTHER_OPTION_IDS.has(id as Id),
  };
};
